import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Trash2 } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { api } from "../../lib/api";

export const PAY_KINDS = { payment: "دفعة إيجار", deposit: "استلام تأمين", deposit_refund: "رد تأمين", deposit_apply: "خصم من التأمين" };
const METHODS = { cash: "نقدي", transfer: "تحويل بنكي", card: "شبكة / بطاقة" };
const fmt = (n) => Number(n || 0).toLocaleString("en-US");

export const RentalPaymentsDialog = ({ contract, canEdit, onClose, onChanged }) => {
  const [c, setC] = useState(contract);
  const [f, setF] = useState({ amount: "", kind: "payment", method: "cash", note: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { setC(contract); setF({ amount: "", kind: "payment", method: "cash", note: "" }); }, [contract]);
  const add = async () => {
    if (!Number(f.amount)) { toast.warning("أدخل المبلغ"); return; }
    setBusy(true);
    try { const r = await api.rentalAddPayment(c.id, { ...f, amount: Number(f.amount) }); setC(r); setF({ ...f, amount: "", note: "" }); toast.success("تم التسجيل"); onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || "تعذّر التسجيل"); } finally { setBusy(false); }
  };
  const del = async (p) => {
    if (!window.confirm("حذف هذه الحركة؟")) return;
    try { setC(await api.rentalDeletePayment(c.id, p.id)); onChanged(); } catch { toast.error("تعذّر الحذف"); }
  };
  if (!c) return null;
  return (
    <Dialog open={!!contract} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg font-body max-h-[90vh] overflow-y-auto" dir="rtl" data-testid="rental-payments-dialog">
        <DialogHeader><DialogTitle>الدفعات والتأمين — {c.number}</DialogTitle><DialogDescription>{c.renter_name}{c.salary_deduct ? " · يُسدد بالخصم من الراتب" : ""}</DialogDescription></DialogHeader>
        <div className="grid grid-cols-4 gap-2 text-center text-xs">
          {[["الإجمالي", c.grand_total, "text-slate-800"], ["المدفوع", c.paid, "text-emerald-600"], ["المتبقي", c.balance, "text-rose-600"], ["التأمين المحتجز", c.deposit_held, "text-violet-600"]].map(([l, v, cls]) => (
            <div key={l} className="rounded-xl bg-slate-50 border border-slate-200 py-2"><div className="text-slate-400">{l}</div><div className={`font-bold tabular-nums text-sm ${cls}`} data-testid={`rental-pay-${l}`}>{fmt(v)}</div></div>
          ))}
        </div>
        {c.deposit_refund_due > 0 && <div className="rounded-lg bg-violet-50 text-violet-700 text-sm px-3 py-2">مستحق للمستأجر: رد تأمين {fmt(c.deposit_refund_due)}</div>}
        {canEdit && c.status !== "cancelled" && (
          <div className="rounded-xl border border-slate-200 p-3 space-y-2">
            <div className="grid grid-cols-3 gap-2">
              <div className="space-y-1"><Label className="text-xs">النوع</Label>
                <select data-testid="rental-pay-kind" value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })} className="h-9 w-full rounded-md border border-slate-200 bg-white px-2 text-sm">
                  {Object.entries(PAY_KINDS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></div>
              <div className="space-y-1"><Label className="text-xs">المبلغ</Label><Input data-testid="rental-pay-amount" type="number" dir="ltr" className="h-9" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} /></div>
              <div className="space-y-1"><Label className="text-xs">الطريقة</Label>
                <select data-testid="rental-pay-method" value={f.method} onChange={(e) => setF({ ...f, method: e.target.value })} className="h-9 w-full rounded-md border border-slate-200 bg-white px-2 text-sm">
                  {Object.entries(METHODS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></div>
            </div>
            <div className="flex gap-2"><Input data-testid="rental-pay-note" className="h-9" placeholder="ملاحظة" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} />
              <Button data-testid="rental-pay-add" disabled={busy} onClick={add} className="h-9 bg-blue-600 hover:bg-blue-700 gap-1">{busy && <Loader2 className="h-4 w-4 animate-spin" />} تسجيل</Button></div>
          </div>
        )}
        <div className="space-y-1.5">
          {(c.payments || []).length === 0 && <p className="text-center text-sm text-slate-400 py-3">لا توجد حركات</p>}
          {(c.payments || []).map((p) => (
            <div key={p.id} data-testid={`rental-pay-row-${p.id}`} className="flex items-center justify-between rounded-lg border border-slate-100 px-3 py-2 text-sm">
              <div><span className="font-semibold text-slate-700">{PAY_KINDS[p.kind]}</span> <span className="text-xs text-slate-400">· {METHODS[p.method] || p.method} · {p.date}{p.note ? ` · ${p.note}` : ""}</span></div>
              <div className="flex items-center gap-2"><span className="font-bold tabular-nums">{fmt(p.amount)}</span>
                {canEdit && <button onClick={() => del(p)} className="text-slate-300 hover:text-rose-600"><Trash2 className="h-3.5 w-3.5" /></button>}</div>
            </div>
          ))}
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>إغلاق</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const RentalExtendDialog = ({ contract, onClose, onDone }) => {
  const [end, setEnd] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (contract) setEnd(contract.end_date); }, [contract]);
  const save = async () => {
    setBusy(true);
    try { const r = await api.rentalExtend(contract.id, end); toast.success(`تم التمديد — الإجمالي الجديد ${fmt(r.total)}`); onDone(r); }
    catch (e) { toast.error(e?.response?.data?.detail || "تعذّر التمديد"); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!contract} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-sm font-body" dir="rtl" data-testid="rental-extend-dialog">
        <DialogHeader><DialogTitle>تمديد العقد — {contract?.number}</DialogTitle><DialogDescription>النهاية الحالية: {contract?.end_date}. يُعاد حساب المبلغ بنفس السعر.</DialogDescription></DialogHeader>
        <Input data-testid="rental-extend-date" type="date" dir="ltr" value={end} onChange={(e) => setEnd(e.target.value)} />
        <DialogFooter className="flex-row-reverse gap-2 sm:justify-start">
          <Button data-testid="rental-extend-save" disabled={busy} onClick={save} className="bg-blue-600 hover:bg-blue-700">تمديد</Button>
          <Button variant="outline" onClick={onClose}>إلغاء</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
